import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { formatLocalizedString, formatCurrency } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import { PROPOSAL_ITEM_PURPOSE } from '../../utils/constants';

import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ProposalStyleClassNames } from '../../ProposalStyleClassNames';
import { ProposalFieldLabelCell } from '../../../../views/components/cells';
import ProposalTableData from '@/types/proposal-table-data';
import LocalizedString from '@/types/localized-string';

export default class RebalanceAnalysisDAO extends ComparisonItemBasedProposalDAO implements ProposalDAOInterface {
  eligibleComparisonItemDAOs?: ComparisonItemDAO[];

  currentPortfolioDAO?: ComparisonItemDAO;

  tableData: ProposalTableData = { rows: [] };

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new RebalanceAnalysisDAO(proposal, proposalElement);
  }

  protected getColorKeys(): string[] {
    return ['DEFAULT'];
  }

  protected build(): void {
    const daoConfig: ProposalNS.RebalanceAnalysisConfig = this.proposalElement.DAO as ProposalNS.RebalanceAnalysisConfig;

    // Filter item DAOs to only include those that have purpose flags
    this.eligibleComparisonItemDAOs = this.getItemDAOs().filter(
      dao => dao.item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && dao.item.flags?.some(flag => Object.values<string>(PROPOSAL_ITEM_PURPOSE).includes(flag)),
    );

    this.currentPortfolioDAO = this.eligibleComparisonItemDAOs.find(dao => dao.item.flags?.includes(PROPOSAL_ITEM_PURPOSE.CURRENT));
    const recommendedItemDAOs = this.eligibleComparisonItemDAOs.filter(dao => dao.item.flags?.includes(PROPOSAL_ITEM_PURPOSE.RECOMMENDED));

    if (!this.currentPortfolioDAO || !recommendedItemDAOs) {
      return;
    }

    if (this.eligibleComparisonItemDAOs.length <= 1) {
      return;
    }

    daoConfig.holdings?.forEach((holding, index) => {
      if (!this.currentPortfolioDAO) {
        return;
      }

      const classes = [ProposalStyleClassNames.ProposalTableContent] as string[];

      classes.push(index % 2 ? ProposalStyleClassNames.ProposalOdd : ProposalStyleClassNames.ProposalEven);

      let color: string | undefined = '#8E959D';
      if (!daoConfig.showDivestedHoldings) {
        color = this.colorsDict !== null ? this.colorsDict[index % Object.keys(this.colorsDict).length] : undefined;
      }

      const change = Math.sign(holding.amountAdjustment) === -1 ? 'negative' : 'positive';

      this.addHoldingNameValue(this.currentPortfolioDAO, holding.name, index, [...classes, ProposalStyleClassNames.ProposalTableFieldName], color); // Holding Name
      this.addPercentValue(this.currentPortfolioDAO, holding.weight, index, 2, classes); // Current Holding %
      this.addDollarsValue(this.currentPortfolioDAO, holding.amount, index, 2, classes); // Current Holding $
      this.addPercentValue(this.currentPortfolioDAO, holding.targetWeight, index, 2, classes); // Target Holding %
      this.addDollarsValue(this.currentPortfolioDAO, holding.targetAmount, index, 2, classes); // Target Holding $
      this.addPercentValue(this.currentPortfolioDAO, holding.weightAdjustment, index, 2, [...classes, change]); // Change %
      this.addDollarsValue(this.currentPortfolioDAO, holding.amountAdjustment, index, 2, [...classes, change]); // Change $
    });

    if (daoConfig.showDivestedHoldings && !daoConfig.showRebalancedHoldings) {
      this.tableData.caption = this.formatMessage('Proposal.Modules.RebalanceAnalysis.Divest');
    } else {
      this.tableData.caption = this.formatMessage('Proposal.Modules.RebalanceAnalysis.RecommendedPortfolio', {
        name: formatLocalizedString(daoConfig.name),
      });
    }
  }

  getTableData(): ProposalTableData {
    const data: ProposalTableData = this.tableData;

    if (!this.eligibleComparisonItemDAOs || this.eligibleComparisonItemDAOs.length <= 1) {
      return data;
    }

    data.colgroup = {
      cols: [
        {
          className: 'col-holding-name',
        },
        {
          className: 'col-current-holding-percentage',
        },
        {
          className: 'col-current-holding-dollars',
        },
        {
          className: 'col-target-holding-percentage',
        },
        {
          className: 'col-target-holding-dollars',
        },
        {
          className: 'col-change-percentage',
        },
        {
          className: 'col-change-dollars',
        },
      ],
    };

    data.header = {
      data: [
        {
          value: this.formatMessage('Proposal.Modules.RebalanceAnalysis.HoldingName'),
          className: 'holding-heading',
        },
        {
          value: this.formatMessage('Proposal.Modules.RebalanceAnalysis.CurrentHolding'),
          className: 'span-heading',
          colSpan: 2,
        },
        {
          value: this.formatMessage('Proposal.Modules.RebalanceAnalysis.TargetHolding'),
          className: 'span-heading',
          colSpan: 2,
        },
        {
          value: this.formatMessage('Proposal.Modules.RebalanceAnalysis.Change'),
          className: 'span-heading',
          colSpan: 2,
        },
      ],
    };

    data.rows = this.currentPortfolioDAO?.getTableColumnData() ?? [];

    return data;
  }

  addStringValue(itemDAO: ComparisonItemDAO, rawString: LocalizedString | undefined, index: number, classes: string[] = []) {
    let str: string = '';
    if (rawString !== undefined) {
      str = formatLocalizedString(rawString, this.locale);
    }
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO(str, str, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  /* eslint-disable-next-line class-methods-use-this */
  addPercentValue(itemDAO: ComparisonItemDAO, value: number, index: number, decimals: number = 2, classes: string[] = []) {
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', this.formatPercentage(value, decimals), this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  addDollarsValue(itemDAO: ComparisonItemDAO, value?: number, index?: number, decimals: number = 2, classes: string[] = []) {
    let strValue = '';
    if (value != null) {
      strValue = formatCurrency(value, decimals, undefined, false, this.locale);
    }

    const holdingField: ProposalFieldDAO = new ProposalFieldDAO('', strValue, this.mergeClasses(classes));
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  addHoldingNameValue(itemDAO: ComparisonItemDAO, rawString: LocalizedString | undefined, index: number, classes: string[] = [], color?: string | null) {
    let str: string = '';
    if (rawString !== undefined) {
      str = formatLocalizedString(rawString, this.locale);
    }
    const holdingField: ProposalFieldDAO = new ProposalFieldDAO(str, str, this.mergeClasses(classes), ProposalFieldLabelCell);
    holdingField.color = color ?? undefined;
    this.addFieldDAO(itemDAO, holdingField, index);
  }

  mergeClasses = (classes: string[]) => classes.join(' ');
}
